import { useEffect, useMemo, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Phone, MessageCircle, Check, X, PhoneOff, ChevronLeft, ChevronRight, Printer, Calendar as CalIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ReminderPrintSheet } from "@/components/ReminderPrintSheet";

const ymd = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const OUTCOMES = [
  { k: "ok",          label: "Contattato",            color: "#10B981", Icon: Check },
  { k: "no_answer",   label: "Non risponde",          color: "#F59E0B", Icon: PhoneOff },
  { k: "unreachable", label: "Non raggiungibile",     color: "#F43F5E", Icon: X },
];
const METHODS = [
  { k: "phone",    label: "Telefono",  Icon: Phone,         color: "#2563EB" },
  { k: "whatsapp", label: "WhatsApp",  Icon: MessageCircle, color: "#25D366" },
];

export default function ReminderPage() {
  const { user } = useAuth();
  const allowed = user && ["admin", "manager", "receptionist"].includes(user.role);
  const [target, setTarget] = useState(() => addDays(new Date(), 1));
  const [apts, setApts] = useState([]);
  const [users, setUsers] = useState([]);
  const [types, setTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState({});

  const dayIso = ymd(target);
  const startIso = `${dayIso}T00:00:00`;
  const endIso = `${dayIso}T23:59:59`;

  const load = useCallback(async () => {
    try {
      const [c, t, u, ac, at] = await Promise.all([
        api.get("/clients"),
        api.get("/appointment-types"),
        api.get("/users"),
        api.get("/appointments", { params: { start: startIso, end: endIso, calendar: "consulenti" } }),
        api.get("/appointments", { params: { start: startIso, end: endIso, calendar: "trainers" } }),
      ]);
      setClients(c.data); setTypes(t.data); setUsers(u.data);
      setApts([
        ...ac.data.map(a => ({ ...a, _cal: "consulenti" })),
        ...at.data.map(a => ({ ...a, _cal: "trainers" })),
      ].sort((a, b) => new Date(a.start) - new Date(b.start)));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [startIso, endIso]);

  useEffect(() => { if (allowed) load(); }, [load, allowed]);

  const patch = async (a, body) => {
    setBusy(b => ({ ...b, [a.id]: true }));
    try {
      const payload = { ...body };
      if (payload.reminder_outcome) {
        payload.reminder_contacted_at = new Date().toISOString();
        payload.reminder_contacted_by = user?.id;
        if (!a.reminder_contact_time) {
          payload.reminder_contact_time = new Date().toTimeString().slice(0, 5);
        }
      }
      const { data } = await api.patch(`/appointments/${a.id}`, payload);
      setApts(list => list.map(x => x.id === a.id ? { ...data, _cal: x._cal } : x));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(b => { const { [a.id]: _, ...rest } = b; return rest; }); }
  };

  const groups = useMemo(() => ({
    consulenti: apts.filter(a => a._cal === "consulenti"),
    trainers: apts.filter(a => a._cal === "trainers"),
  }), [apts]);

  const total = apts.length;
  const done = apts.filter(a => !!a.reminder_outcome).length;
  const pct = total ? Math.round(done / total * 100) : 0;

  const headerLabel = target.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const isFuture = ymd(target) > ymd(new Date());
  const isToday = ymd(target) === ymd(new Date());

  if (user && !allowed) return <Navigate to="/" replace />;

  return (
    <div className="h-full overflow-y-auto print:h-auto print:overflow-visible">
      <ReminderPrintSheet headerLabel={headerLabel} groups={groups} clients={clients} types={types} users={users} />
      <div className="p-6 max-w-6xl mx-auto print:hidden">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Chiamate del Giorno Prima</h1>
            <p className="text-muted-foreground text-sm mt-1">Riepilogo appuntamenti del giorno successivo per contattare i clienti.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => window.print()} data-testid="reminders-print-button">
              <Printer className="h-4 w-4 mr-2" /> Stampa
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setTarget(d => addDays(d, -1))} data-testid="reminders-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setTarget(addDays(new Date(), 1))}>Domani</Button>
              <Button variant="outline" size="sm" onClick={() => setTarget(new Date())}>Oggi</Button>
              <Button variant="outline" size="icon" onClick={() => setTarget(d => addDays(d, 1))} data-testid="reminders-next-day"><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <Input type="date" value={dayIso} onChange={(e) => setTarget(new Date(e.target.value + "T12:00:00"))} className="w-44" data-testid="reminders-date-input" />
            <div className="flex items-center gap-2 ml-auto">
              <CalIcon className="h-4 w-4 text-muted-foreground" />
              <div className="font-display font-bold capitalize">{headerLabel}</div>
              {isFuture && <Badge className="bg-blue-600">Da chiamare</Badge>}
              {isToday && <Badge className="bg-amber-500">Oggi</Badge>}
            </div>
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Avanzamento Chiamate</div>
              <div className="font-mono text-sm font-bold" data-testid="reminders-progress-text">{done} / {total} · {pct}%</div>
            </div>
            <Progress value={pct} className="h-2" />
          </CardContent>
        </Card>

        {total === 0 && (
          <div className="text-center py-10 text-muted-foreground border border-dashed rounded-lg">
            Nessun appuntamento in questa giornata.
          </div>
        )}

        {["consulenti", "trainers"].map((cal) => {
          const list = groups[cal] || [];
          if (!list.length) return null;
          return (
            <div key={cal} className="mb-6">
              <div className="text-xs uppercase font-bold tracking-widest text-slate-700 mb-2 flex items-center gap-2">
                {cal === "trainers" ? "Calendario Trainers" : "Calendario Consulenti"}
                <Badge variant="secondary">{list.length}</Badge>
              </div>
              <div className="space-y-2">
                {list.map(a => (
                  <ReminderRow
                    key={a.id}
                    apt={a}
                    client={clients.find(c => c.id === a.client_id)}
                    aptType={types.find(t => t.id === a.appointment_type_id)}
                    collab={users.find(u => u.id === a.collaborator_id)}
                    onPatch={patch}
                    busy={!!busy[a.id]}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReminderRow({ apt, client, aptType, collab, onPatch, busy }) {
  const s = new Date(apt.start);
  const phone = client?.phone || "";
  const name = (client && `${client.first_name || client.name || ""} ${client.last_name || ""}`.trim()) || apt.title || "—";
  return (
    <Card className={busy ? "opacity-70" : ""} data-testid={`reminder-row-${apt.id}`}>
      <CardContent className="p-3 flex items-center gap-3 flex-wrap">
        {/* ORARIO SLOT */}
        <div className="font-mono text-xs font-bold text-slate-600 w-16 shrink-0">
          {s.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
        </div>
        {/* CLIENTE + TIPO */}
        <div className="flex-1 min-w-[180px]">
          <div className="font-semibold truncate">{name}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
            {aptType && (
              <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide" style={{ color: aptType.color }}>
                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: aptType.color }} />
                {aptType.name}
              </span>
            )}
            {collab && <span>· {collab.name}</span>}
          </div>
        </div>
        {/* TELEFONO */}
        <div className="font-mono text-sm w-36 shrink-0">
          {phone ? (
            <a href={`tel:${phone}`} className="text-blue-700 hover:underline">{phone}</a>
          ) : (
            <span className="text-muted-foreground italic">—</span>
          )}
        </div>
        {/* METODO + ESITO */}
        <div className="flex items-center gap-1">
          {METHODS.map(m => {
            const active = apt.reminder_method === m.k;
            return (
              <button key={m.k}
                onClick={() => onPatch(apt, { reminder_method: active ? "" : m.k })}
                disabled={busy}
                title={m.label}
                data-testid={`reminder-method-${m.k}-${apt.id}`}
                className={`h-8 w-8 rounded flex items-center justify-center transition-colors ${active ? "text-white" : "text-slate-500 hover:bg-slate-100"}`}
                style={active ? { backgroundColor: m.color } : {}}
              >
                <m.Icon className="h-4 w-4" />
              </button>
            );
          })}
          <div className="w-px h-6 bg-slate-200 mx-1" />
          {OUTCOMES.map(o => {
            const active = apt.reminder_outcome === o.k;
            return (
              <button key={o.k}
                onClick={() => onPatch(apt, { reminder_outcome: active ? "" : o.k })}
                disabled={busy}
                title={o.label}
                data-testid={`reminder-outcome-${o.k}-${apt.id}`}
                className={`h-8 px-2 rounded flex items-center gap-1 text-xs font-semibold transition-colors ${active ? "text-white" : "text-slate-500 hover:bg-slate-100"}`}
                style={active ? { backgroundColor: o.color } : {}}
              >
                <o.Icon className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">{o.label}</span>
              </button>
            );
          })}
          <Input
            type="time"
            value={apt.reminder_contact_time || ""}
            onChange={(e) => onPatch(apt, { reminder_contact_time: e.target.value })}
            className="w-24 h-8 text-xs"
            data-testid={`reminder-time-${apt.id}`}
          />
        </div>
      </CardContent>
    </Card>
  );
}
